import { useEffect, useState } from "react"
import { AlertTriangle, Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export type DeleteIntegrationDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  integrationName: string
  secretCount: number
  onConfirm: (confirmName: string) => void | Promise<void>
  isProcessing?: boolean
}

export function DeleteIntegrationDialog({
  open,
  onOpenChange,
  integrationName,
  secretCount,
  onConfirm,
  isProcessing,
}: DeleteIntegrationDialogProps) {
  const [confirmName, setConfirmName] = useState("")

  useEffect(() => {
    if (!open) {
      setConfirmName("")
    }
  }, [open])

  const nameMatches = confirmName.trim() === integrationName.trim()

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (isProcessing) return
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete integration?</DialogTitle>
          <DialogDescription className="text-left">
            This action cannot be undone. The integration and all associated configuration will be
            permanently removed.
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive">
          <AlertTriangle aria-hidden />
          <AlertTitle>All OAuth secrets will be deleted first</AlertTitle>
          <AlertDescription>
            {secretCount === 0 ? (
              <p>This integration has no OAuth secrets.</p>
            ) : (
              <p>
                {secretCount} OAuth secret{secretCount === 1 ? "" : "s"} (active and inactive) will
                be permanently deleted before the integration is removed. Any applications using
                these credentials will stop working immediately.
              </p>
            )}
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="delete-integration-confirm-name">
            Type <span className="font-medium text-foreground">{integrationName}</span> to confirm
          </Label>
          <Input
            id="delete-integration-confirm-name"
            value={confirmName}
            onChange={(event) => setConfirmName(event.target.value)}
            placeholder={integrationName}
            disabled={isProcessing}
            autoComplete="off"
            aria-invalid={confirmName.length > 0 && !nameMatches}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            disabled={isProcessing}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isProcessing || !nameMatches}
            onClick={() => void onConfirm(confirmName.trim())}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Deleting…
              </>
            ) : (
              "Delete integration"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
